'use client';

import Link from 'next/link';
import type { FixtureWithTeams } from '../lib/actions';
import { flag } from '../lib/flags';

type Props = { fixture: FixtureWithTeams };

function fmtDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

function fmtTime(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

export function MatchCard({ fixture: f }: Props) {
  const hasPred = f.home_win != null;

  const hp = hasPred ? Math.round((f.home_win as number) * 100) : 0;
  const dp = hasPred ? Math.round((f.draw as number) * 100) : 0;
  const ap = hasPred ? Math.round((f.away_win as number) * 100) : 0;

  let highIdx = 0, lowIdx = 0;
  if (hasPred) {
    const vals = [hp, dp, ap];
    highIdx = vals.indexOf(Math.max(...vals));
    lowIdx = vals.indexOf(Math.min(...vals));
  }
  const colors = ['var(--text)', 'var(--text)', 'var(--text)'];
  const bgs = ['rgba(255,255,255,0.03)', 'rgba(255,255,255,0.03)', 'rgba(255,255,255,0.03)'];
  if (hasPred) {
    colors[highIdx] = 'var(--win)';
    colors[lowIdx] = 'var(--loss)';
    if (highIdx !== 1 && lowIdx !== 1) colors[1] = 'var(--draw)';
    bgs[highIdx] = 'rgba(0,229,176,0.07)';
    bgs[lowIdx] = 'rgba(239,68,68,0.07)';
    if (highIdx !== 1 && lowIdx !== 1) bgs[1] = 'rgba(245,158,11,0.07)';
  }

  return (
    <Link href={`/partido/${f.id}`}>
      <div className="card match-card-hover" style={{ cursor: 'pointer', transition: 'border-color 0.15s' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span className="label">{f.group_id ? `GRUPO ${f.group_id}` : f.round}</span>
          <span style={{ fontSize: 10, color: 'var(--muted)' }}>
            {fmtDate(f.kickoff_at)} · {fmtTime(f.kickoff_at)}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8, marginBottom: hasPred ? 12 : 0 }}>
          <div>
            <div style={{ fontSize: 22, lineHeight: 1 }}>{flag(f.home_code)}</div>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.04em', marginTop: 4 }}>{f.home_code}</div>
            <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 2 }}>{f.home_name}</div>
          </div>
          <div style={{ textAlign: 'center', padding: '0 8px' }}>
            {f.status === 'finished' && f.home_goals != null ? (
              <span style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-0.02em' }}>
                {f.home_goals} – {f.away_goals}
              </span>
            ) : (
              <span style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '0.1em', fontWeight: 700 }}>VS</span>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, lineHeight: 1, textAlign: 'right' }}>{flag(f.away_code)}</div>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.04em', marginTop: 4 }}>{f.away_code}</div>
            <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 2 }}>{f.away_name}</div>
          </div>
        </div>

        {hasPred && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
            {[
              { label: '1', val: hp, color: colors[0], bg: bgs[0] },
              { label: 'X', val: dp, color: colors[1], bg: bgs[1] },
              { label: '2', val: ap, color: colors[2], bg: bgs[2] },
            ].map(({ label, val, color, bg }) => (
              <div key={label} style={{
                textAlign: 'center', padding: '7px 4px',
                background: bg,
                border: '1px solid var(--border)',
                borderRadius: 3,
              }}>
                <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color }}>{val}%</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
